<template lang="pug">
div(:class="dynamicClasses.wrapper")
  button(
    ref="button"
    type="button"
    :id="buttonUid"
    :class="dynamicClasses.button"
    aria-haspopup="true"
    :aria-label="label"
    :aria-expanded="open.toString()"
    :aria-controls="menuUid"
    :aria-hidden="isDisabled.toString()"
    :disabled="isDisabled"
    v-click-outside="closeMenu"
    @click="onClick"
    @keydown="onKeydown"
  )
    slot
    CardBrandArrow(
      v-show="hasMultipleBrands"
      :activated="hasMultipleBrands && open"
    )
  CardBrandSelectorMenu(
    ref="menu"
    v-model="selectedBrand"
    v-on:close="closeMenu(true)"
    v-on:user-action="onUserAction"
    :aria-hidden="(!open).toString()"
    :selector-context="selectorContext"
    :open="open"
    :button-uid="buttonUid"
    :menu-uid="menuUid"
    :brand-objects="brandObjects"
  )
</template>

<script>
import { mapGetters } from 'vuex'

import PreloadedAssets from '@/configuration/PreloadedAssets'
import { assets } from '@/common/loader/assets'
import { ensureUniqId } from '@/common/util/svg'
import Events from '@/configuration/Events'

import CardBrandSelectorMenu from '@/host/components/controls/cardbrandselector/Menu'
import CardBrandArrow from '@/host/components/controls/cardbrandselector/Arrow'

import { ClickOutsideMixin } from '@/host/components/mixins/ClickOutside'
import BrandMixin from '@/common/components/mixins/Brand'

/**
 * Based on W3 example:
 * https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-actions-active-descendant/
 *
 * @since KJS-5014
 */
export default {
  name: 'CardBrandSelectorButton',
  components: {
    CardBrandSelectorMenu,
    CardBrandArrow
  },
  mixins: [BrandMixin, ClickOutsideMixin],
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      brandObjects: [{ brand: 'DEFAULT' }],
      open: false,
      keyPressed: false
    }
  },
  computed: {
    ...mapGetters(['translate']),
    dynamicClasses() {
      return {
        wrapper: {
          'kr-open': this.open,
          'kr-multi-brand': this.hasMultipleBrands
        },
        button: {
          'kr-open': this.open,
          'kr-multi-brand': this.hasMultipleBrands
        }
      }
    },
    selectorContext() {
      return {
        keyPressed: this.keyPressed
      }
    },
    hasMultipleBrands() {
      // HACK KJS-1998, if OSB don't display multiple brands
      const isOsbBrand = this.selectedBrand?.split('_')[0] === 'PRV'

      return this.detectedBrands.length > 1 && !isOsbBrand
    },
    isDisabled() {
      return this.disabled || !this.hasMultipleBrands
    },
    buttonUid() {
      return `kr-cbs-btn-${this._uid}`
    },
    menuUid() {
      return `kr-cbs-menu-${this._uid}`
    },
    label() {
      return this.translate('label_brand_selector_button')
    }
  },
  watch: {
    detectedBrands: 'buildBrandObjects',
    selectedBrand: 'closeMenu'
  },
  created() {
    // The click-outside does not allow to detect a click outside of current
    // frame, meaning a click in an iframe cannot be detected with a click
    // handler un current document.
    this.$busOn(Events.krypton.field.event, ({ type }) => {
      if (this.open && ['focusReport', 'focus'].includes(type)) {
        this.closeMenu()
      }
    })
  },
  methods: {
    toggleMenu() {
      this.open = !this.open
    },
    openMenu() {
      this.open = true
    },
    /**
     * @param restoreFocus {boolean} | We should restore focus only for a
     * closure triggered from keyboard.
     */
    closeMenu(restoreFocus = false) {
      if (this.open) {
        this.open = false

        if (restoreFocus) {
          this.$refs.button.focus()
        }
      }
    },
    onUserAction(keyPressed) {
      this.keyPressed = keyPressed
    },
    onClick() {
      this.keyPressed = false
      this.toggleMenu()
    },
    onKeydown(event) {
      this.keyPressed = true

      let flag = false

      if (event.ctrlKey || event.altKey || event.metaKey) {
        return
      }

      switch (event.key) {
        case ' ':
        case 'Enter':
        case 'ArrowDown':
        case 'Down':
        case 'Up':
        case 'ArrowUp':
          this.openMenu()
          flag = true
          break
        case 'Esc':
        case 'Escape':
          this.closeMenu()
          flag = true
          break
      }

      if (flag) {
        event.stopPropagation()
        event.preventDefault()
      }
    },
    async buildBrandObjects() {
      const { regular } = assets
      const brandObjects = []
      for (const brand of this.detectedBrands) {
        const iconDef = regular.branded[brand]
        const icon = ensureUniqId(
          (iconDef && (await iconDef())) || PreloadedAssets.regular.pan
        )
        brandObjects.push({ brand, icon })
      }
      this.brandObjects = brandObjects
    }
  }
}
</script>
